import React from "react";
import { Controller } from "react-hook-form";
import { isLimitedAdmin } from "../../lib/roleUtils";

// ─── Reusable Section Divider (matches AddCustomer pattern) ───────────────────
const Section = ({ title, children }) => (
    <div className="mb-4">
        <h2 className="text-center fw-bold text-uppercase fs-6 py-2 mb-3 border-bottom border-top gtss-text-primary">
            {title}
        </h2>
        {children}
    </div>
);

const Step2Addresses = ({
    control,
    partyName,
    foundCustomer,
    selectedShippingIndex,
    setSelectedShippingIndex,
    handleAddressSelect,
    goToPreviousStep,
    goToNextStep,
    role,
    isLoading,
    updateLoading
}) => {
    return (
        <div className="card shadow-sm border rounded-top-0 rounded-bottom-3 p-3 p-md-4 d-flex flex-column justify-content-between flex-grow-1">
            <div>

            {/* ── Container & Lorry ── */}
            <div className="row g-3 mb-4">
                <div className="col-12 col-md-6">
                    <label htmlFor="pl-container" className="form-label fw-semibold small text-uppercase gtss-text-primary">
                        Container No
                    </label>
                    <Controller
                        control={control}
                        name="container"
                        render={({ field }) => (
                            <input
                                {...field}
                                id="pl-container"
                                type="text"
                                className="form-control"
                                placeholder="Enter container number"
                            />
                        )}
                    />
                </div>
                <div className="col-12 col-md-6">
                    <label htmlFor="pl-lorry" className="form-label fw-semibold small text-uppercase gtss-text-primary">
                        Lorry
                    </label>
                    <Controller
                        control={control}
                        name="lorry"
                        render={({ field }) => (
                            <input
                                {...field}
                                id="pl-lorry"
                                type="text"
                                className="form-control"
                                placeholder="Enter lorry number"
                            />
                        )}
                    />
                </div>
            </div>

            {/* ── Bill Details ── */}
            <Section title="Bill Details">
                <div className="row g-3">
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="pl-fromName" className="form-label fw-semibold small text-uppercase gtss-text-primary">Name</label>
                        <Controller control={control} name="fromName"
                            render={({ field }) => (
                                <input {...field} id="pl-fromName" type="text" className="form-control" placeholder="Enter name" />
                            )}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="pl-fromMobile" className="form-label fw-semibold small text-uppercase gtss-text-primary">Number</label>
                        <Controller control={control} name="fromNumber"
                            render={({ field }) => (
                                <input {...field} id="pl-fromMobile" type="text" className="form-control" placeholder="Enter door number" />
                            )}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="pl-fromStreet" className="form-label fw-semibold small text-uppercase gtss-text-primary">Street</label>
                        <Controller control={control} name="fromStreet"
                            render={({ field }) => (
                                <input {...field} id="pl-fromStreet" type="text" className="form-control" placeholder="Enter street" />
                            )}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="pl-fromLandmark" className="form-label fw-semibold small text-uppercase gtss-text-primary">Landmark</label>
                        <Controller control={control} name="fromLandmark"
                            render={({ field }) => (
                                <input {...field} id="pl-fromLandmark" type="text" className="form-control" placeholder="Enter landmark" />
                            )}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="pl-fromCity" className="form-label fw-semibold small text-uppercase gtss-text-primary">City</label>
                        <Controller control={control} name="fromCity"
                            render={({ field }) => (
                                <input {...field} id="pl-fromCity" type="text" className="form-control" placeholder="Enter city" />
                            )}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="pl-fromDistrict" className="form-label fw-semibold small text-uppercase gtss-text-primary">District</label>
                        <Controller control={control} name="fromDistrict"
                            render={({ field }) => (
                                <input {...field} id="pl-fromDistrict" type="text" className="form-control" placeholder="Enter district" />
                            )}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="pl-fromPincode" className="form-label fw-semibold small text-uppercase gtss-text-primary">Pin-Code</label>
                        <Controller control={control} name="fromPincode"
                            render={({ field }) => (
                                <input {...field} id="pl-fromPincode" type="text" className="form-control" placeholder="Enter pin code" />
                            )}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="pl-gstNumber" className="form-label fw-semibold small text-uppercase gtss-text-primary">GST Number</label>
                        <Controller control={control} name="gstNumber"
                            render={({ field }) => (
                                <input {...field} id="pl-gstNumber" type="text" className="form-control" placeholder="Enter GST number" />
                            )}
                        />
                    </div>
                </div>
            </Section>

            {/* ── Shipping Details ── */}
            <Section title="Shipping Details">
                {/* Address selector dropdown */}
                {partyName && (
                    <div className="mb-3">
                        <label className="form-label fw-semibold small text-uppercase gtss-text-primary">
                            Select Shipping Address
                        </label>
                        <select
                            id="pl-shippingSelect"
                            className="form-select"
                            value={selectedShippingIndex ?? ""}
                            onChange={(e) => {
                                const index = e.target.value;
                                if (index === "") {
                                    handleAddressSelect(null, null);
                                    setSelectedShippingIndex(null);
                                } else {
                                    const selectedAddress = foundCustomer?.shippinglist?.[index];
                                    handleAddressSelect(partyName, selectedAddress);
                                    setSelectedShippingIndex(index);
                                }
                            }}
                        >
                            <option value="">Shipping Details</option>
                            {foundCustomer?.shippinglist?.map((address, index) => (
                                <option key={index} value={index}>
                                    {`${address?.shippingAddressMobNo}, ${address?.shippingAddressDistrict}`}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="row g-3">
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="pl-toName" className="form-label fw-semibold small text-uppercase gtss-text-primary">To Name</label>
                        <Controller control={control} name="toName"
                            render={({ field }) => (
                                <input {...field} id="pl-toName" type="text" className="form-control" placeholder="Enter name" />
                            )}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="pl-toMobile" className="form-label fw-semibold small text-uppercase gtss-text-primary">Number</label>
                        <Controller control={control} name="toNumber"
                            render={({ field }) => (
                                <input {...field} id="pl-toMobile" type="tel" className="form-control" placeholder="Enter number" />
                            )}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="pl-toStreet" className="form-label fw-semibold small text-uppercase gtss-text-primary">Street</label>
                        <Controller control={control} name="toStreet"
                            render={({ field }) => (
                                <input {...field} id="pl-toStreet" type="text" className="form-control" placeholder="Enter street" />
                            )}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="pl-toArea" className="form-label fw-semibold small text-uppercase gtss-text-primary">Area</label>
                        <Controller control={control} name="toLandmark"
                            render={({ field }) => (
                                <input {...field} id="pl-toArea" type="text" className="form-control" placeholder="Enter landmark/area" />
                            )}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="pl-toCity" className="form-label fw-semibold small text-uppercase gtss-text-primary">City</label>
                        <Controller control={control} name="toCity"
                            render={({ field }) => (
                                <input {...field} id="pl-toCity" type="text" className="form-control" placeholder="Enter city" />
                            )}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="pl-toState" className="form-label fw-semibold small text-uppercase gtss-text-primary">State</label>
                        <Controller control={control} name="toDistrict"
                            render={({ field }) => (
                                <input {...field} id="pl-toState" type="text" className="form-control" placeholder="Enter state/district" />
                            )}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="pl-toPincode" className="form-label fw-semibold small text-uppercase gtss-text-primary">Pin-Code</label>
                        <Controller control={control} name="toPincode"
                            render={({ field }) => (
                                <input {...field} id="pl-toPincode" type="text" className="form-control" placeholder="Enter pin code" />
                            )}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="pl-distance" className="form-label fw-semibold small text-uppercase gtss-text-primary">Distance</label>
                        <Controller control={control} name="distance"
                            render={({ field }) => (
                                <input {...field} id="pl-distance" type="number" step="any" className="form-control" placeholder="0" />
                            )}
                        />
                    </div>
                </div>
            </Section>
        </div>

            {/* ── Navigation Buttons ── */}
            <div className="text-center pt-2 pb-1 d-flex flex-wrap justify-content-center gap-2 gap-sm-3">
                {!isLimitedAdmin(role) && (
                    <button
                        type="button"
                        id="pl-step2-back-btn"
                        className="btn btn-light px-5 py-2 fw-semibold border"
                        style={{ borderRadius: "20px" }}
                        onClick={goToPreviousStep}
                    >
                        Back
                    </button>
                )}
                <button
                    type="button"
                    id="pl-step2-next-btn"
                    className="btn px-5 py-2 fw-semibold text-white gtss-bg-primary border-0"
                    style={{ borderRadius: "20px" }}
                    onClick={goToNextStep}
                >
                    Next
                </button>
                <button
                    type="submit"
                    id="pl-step2-submit-btn"
                    className="btn px-5 py-2 fw-semibold text-white gtss-bg-accent border-0"
                    style={{ borderRadius: "20px" }}
                    disabled={isLoading || updateLoading}
                >
                    {isLoading || updateLoading ? "Saving..." : "Submit"}
                </button>
            </div>
        </div>
    );
};

export default Step2Addresses;
